import { Form, SchemaFormHook, useForm, useFormContext, useStore } from "@/components/form";
import { Articulo } from "@/shared/types/articulo";
import { CircularProgress, Collapse, Stack, StackProps, Typography } from "@mui/material";
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import { Dialog, ModalContextExtractor, ModalHeader, useOpenModal } from "@/components/modal";
import useFetch from "@/hooks/use-fetch";
import { searchArticulo } from "@/features/inventario/articulos/api/search-articulo";
import { DataCache } from "@/components/display";
import PreviewArticulo from "@/features/inventario/articulos/components/preview-articulo";
import CompraArticuloManualSearch from "./compra-articulo-manual-search";
import { notify, notifyError } from "@/shared/notification";
import { z } from "zod/v4";
import useAfterCall from "@/hooks/use-after-call";
import { AddArticuloMutation } from "@/features/inventario/articulos/api/add-articulo";
import ArticuloForm, { articuloFormSchema } from "@/features/inventario/articulos/components/articulo-form";
import useHasPermisos from "@/features/auth/hooks/useHasPermisos";
import { Permiso } from "@/shared/types/permiso";

interface CompraArticuloSearchProps extends StackProps {
    onArticuloSelect?: (value: {
        articulo: Articulo,
        cantidad: number,
        costo: number
    }) => void
}

const compraArticuloSearchFormSchema = z.object({
    codigo: z.string(),
    cantidad: z.number({ message: "Este campo es requerido" }).min(1, "El valor debe ser mayor a 0"),
    costo: z.number({ message: "Este campo es requerido" }).min(1, "El valor debe ser mayor a 0"),
})

export default function CompraArticuloSearch({
    onArticuloSelect,
    ...props
}: CompraArticuloSearchProps) {
    const openModal = useOpenModal();

    const hasWritePermiso = useHasPermisos(Permiso.WriteArticulos);

    const { value: articulo, loading, error, fetch, reset } = useFetch(async (codigo: string) => {
        if (!codigo) return undefined;
        const result = await searchArticulo(codigo, true);
        return result || null;
    }, {
        onError: (e) => notifyError(e),
        onSuccess: (v) => {
            if (!v) {
                focusField("codigo");
                return;
            }

            form.setFieldValue("codigo", v.codigo);
            form.setFieldValue("costo", v.costo);
            if (!form.getFieldValue("cantidad")) form.setFieldValue("cantidad", 1);
            focusField("cantidad");
        }
    })

    const form = useForm({
        defaultValues: {
            codigo: "",
            cantidad: 1,
            costo: "" as unknown as number
        },
        submitOnce: false,
        closeModalOnSubmit: false,
        validators: {
            onSubmit: compraArticuloSearchFormSchema
        },
        enterBehaviour: "selectNext",
        onSubmit: ({ value, formApi }) => {
            onArticuloSelect && onArticuloSelect({
                articulo: articulo!,
                cantidad: value.cantidad,
                costo: value.costo
            });
            reset();
            formApi.reset();
            focusField("codigo");
        },
        parentForm: useFormContext()
    })

    const codigo = useStore(form.store, state => state.values.codigo);

    const focusField = useAfterCall((name: string) => form.focusField(name, true));

    return <Form form={form} gap={2} {...props}>
        <Stack direction={"row"} alignItems={"center"} justifyContent={"space-between"}>
            <Typography variant="h6">Agregar Artículos</Typography>
            <Stack gap={2} direction={"row"} >
                <Form.IconButton size="small" onClick={() => openModal("searchArticulo")}>
                    <SearchIcon />
                </Form.IconButton>
                {hasWritePermiso && <Form.IconButton size="small" onClick={() => openModal("addArticulo")}>
                    <AddIcon />
                </Form.IconButton>}
            </Stack>
        </Stack>
        <Form.TextField
            name="codigo" label="Código" size="small" disabled={loading}
            onKeyDown={e => {
                if (e.key === "Enter") {
                    e.preventDefault();
                    fetch(codigo);
                }
            }}
            slotProps={{
                input: {
                    endAdornment: <Form.IconButton edge="end" disabled={loading} onClick={() => fetch(codigo)}>
                        {loading && codigo ? <CircularProgress size={20} /> : <SearchIcon />}
                    </Form.IconButton>
                }
            }}
        />
        <Stack direction={"row"} gap={2} flexWrap={"wrap"}>
            <Stack flex={"1 0 150px"}>
                <Form.NumberField name="cantidad" label="Cantidad" size="small" decimalScale={0} alignRight />
            </Stack>
            <Stack flex={"5 0 150px"}>
                <Form.MonedaField name="costo" label="Costo" size="small" />
            </Stack>
        </Stack>
        <Form.SubmitButton disabled={!articulo}>Agregar</Form.SubmitButton>

        <Collapse in={!loading && articulo !== undefined}>
            <DataCache data={articulo}>
                {articulo => <PreviewArticulo
                    articulo={articulo}
                    error={error}
                />}
            </DataCache>
        </Collapse>

        {hasWritePermiso && <Dialog code="addArticulo" size="md" >
            <ModalHeader>Agregar Articulo</ModalHeader>
            <AddArticuloMutation>
                {mutation => <SchemaFormHook
                    schema={articuloFormSchema}
                    onSubmit={async ({ value }) => {
                        await mutation.mutateAsync(value);
                        notify("El artículo se ha agregado correctamente.");
                    }}
                    submitOnce
                    closeModalOnSubmit
                >
                    {form => <ArticuloForm form={form} padding={3} />}
                </SchemaFormHook>}
            </AddArticuloMutation>
        </Dialog>}

        <Dialog code="searchArticulo" size="lg">
            <ModalHeader>Buscar Artículo</ModalHeader>
            <ModalContextExtractor>
                {({ handleClose }) => <CompraArticuloManualSearch
                    padding={3}
                    onArticuloSelect={articulo => {
                        form.setFieldValue("codigo", articulo.codigo)
                        fetch(articulo.codigo);
                        handleClose();
                    }}
                />}
            </ModalContextExtractor>
        </Dialog>
    </Form>
}